import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { cancelBookingRequest, assignTripRequest, getNewBookingsRequest, updateNewBookingRequest, autoAssignTripRequest, resetAutoAssignState, getAvailableCabsRadiusRequest, getSelectedCabRequest } from "../../redux/actions/newAction";
import { Button, Table, Input, Tooltip, Modal, Col, message, Form, Spin, DatePicker, TimePicker, Checkbox, Row, Select, ConfigProvider } from "antd";
import type { TableProps } from "antd";
import {
  RedoOutlined,
  EditOutlined,
  CarOutlined,
  SearchOutlined,
  ThunderboltOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import AssignModel from "../../components/Assignmodel";
import CancelAssign from "../../components/CancelModel";
import "../../style/New.css";
import { styled } from "styled-components";
import {
  getNewBookingEditRequest,
  getNewBookingEditReset,
  getLocationsRequest,
  getVehicleTypesRequest,
  getTripTypesRequest,
  getGoodsTypesRequest,
  getPackagesRequest,
  getIntercityListRequest,
  getNightFareRequest,
  resetFareEstimate,
  clearFareLists,
} from "../../redux/actions/bookingActions";
import { socket } from "../../utils/socket";
import { useBranchId } from "../../utils/hooks/useBranch";
import { useMountEffectOnce } from "../../utils/hooks/useMountEffectOnce";
import { useLocationOptions } from "../../utils/hooks/useLocationOptions";
import LocationSelect from "../../components/LocationSelect";
import { isAppBooking, isFlagOn } from "../../utils/bookingFlags";
import { COPY_CELL } from "../../utils/copyGuard";
import { noRowSelectionBgTheme } from "../../utils/tableTheme";
import { isAdvanceBooking, isDelayedBooking } from "../../utils/dateUtils";
import { getEmployeeName } from "../../utils/currentUser";


interface DataType {
  key: string;
  name: string;
  age: number;
  address: string;
  [key: string]: any;
}

const { Search } = Input;

const NEW_SOCKET_REFRESH_DEBOUNCE_MS = 400;

const pageWrapper: React.CSSProperties = {
  padding: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
};

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  border-radius: 16px;
  margin-bottom: 18px;
  background: rgba(137, 67, 154, 0.45);
  backdrop-filter: blur(10px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  flex-wrap: nowrap;
  gap: clamp(10px, 1vw, 16px);

  /* Below antd lg one row stops being the right answer - fall back to the
     wrapping the toolbar has always done at these sizes. */
  @media (max-width: 991.98px) {
    flex-wrap: wrap;
  }

  @media (max-width: 768px) {
    flex-direction: column;
    padding: 12px;
    align-items: stretch;
  }
`;

const ToolbarGroup = styled.div<{ $maxGap?: number }>`
  display: flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  gap: clamp(8px, 1vw, ${(p) => p.$maxGap ?? 24}px);

  /* Assign / Cancel are a fixed 150px in New.css. Scoped here so only this
     toolbar's copies shrink. */
  .action-btn1,
  .action-btn2 {
    width: clamp(96px, 8.5vw, 150px);
    min-width: 0;
    padding-inline: clamp(4px, 0.6vw, 15px);
    font-size: clamp(12px, 1vw, 16px);
    white-space: nowrap;
  }

  /* The circular Auto Assign and Refresh buttons are already small - they must
     not be squeezed any further. */
  .ant-btn-circle,
  .ant-btn-icon-only {
    flex: 0 0 auto;
  }

  .ant-input-search {
    flex: 0 1 auto;
    min-width: 0;
  }

  @media (max-width: 991.98px) {
    flex-wrap: wrap;
  }
`;

const ActiveTripsPill = styled.div`
  background: linear-gradient(135deg, #89339c, #b56ac8);
  color: white;
  padding: 12px clamp(12px, 1.3vw, 28px);
  border-radius: 9999px;
  font-size: clamp(13px, 1.05vw, 18px);
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: clamp(6px, 0.9vw, 12px);
  white-space: nowrap;
  flex: 0 0 auto;
  box-shadow: 0 6px 20px rgba(147, 51, 234, 0.3);

  /* The count is what anyone actually reads off the pill, so it runs a step
     larger than the "Active Trips :" label beside it. line-height:1 keeps the
     taller text inside the existing line box, so the pill does not grow. */
  .pill-value {
    font-size: clamp(16px, 1.3vw, 21px);
    line-height: 1;
  }

  @media (max-width: 768px) {
    padding: 8px 20px;
    font-size: 16px;
    justify-content: center;
  }
`;

const refreshBtn: React.CSSProperties = {
  height: 44,
  width: 44,
  borderRadius: 12,
  color: "#fff",
  border: "none",
  background: "#87398f",
};

const PillGroup: React.FC<{ value?: string[]; onChange?: (v: string[]) => void }> = ({
  value = [],
  onChange,
}) => {
  const options = ["CV", "Prepaid", "Toll Gate", "Intra"];
  const toggle = (opt: string) => {
    const next = value.includes(opt)
      ? value.filter((v) => v !== opt)
      : [...value, opt];
    onChange?.(next);
  };
  return (
    <div style={{ display: "flex", gap: 7, flexWrap: "wrap", justifyContent: "space-between", alignItems: "center" }}>
      {options.map((opt) => (
        <span
          key={opt}
          onClick={() => toggle(opt)}
          style={{
            padding: "5px 13px",
            border: `0.5px solid ${value.includes(opt) ? "#89339c" : "#d9d9d9"}`,
            borderRadius: 20,
            fontSize: 12,
            cursor: "pointer",
            background: value.includes(opt) ? "rgba(137,51,156,0.1)" : "transparent",
            color: value.includes(opt) ? "#89339c" : "#888",
            userSelect: "none",
            transition: "all 0.12s",
          }}
        >
          {value.includes(opt) ? "✓ " : ""}
          {opt}
        </span>
      ))}
    </div>
  );
};

const convertMinutesToHrs = (minutes: number): string => {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hrs}.${mins === 0 ? "00" : mins} Hrs`;
};

const New: React.FC = () => {
  const dispatch = useDispatch();
  const { newBookings, newBookingsLoading, cancelData, loading: cancelLoading, error, assignData, assignTripLoading, todaycount,
    updateBookingLoading, updateBookingSuccess, updateBookingError,
    autoAssignLoading, autoAssignData, autoAssignError, selectedCab, selectedCabError,
  } = useSelector((state: any) => state.newBooking || {});
  const {
    editBooking, editBookingLoading, editBookingError,
    locations, vehicleTypes, tripTypes, goodsTypes,
    packages, intercityList, nightFare, companies,
  } = useSelector((state: any) => state.booking);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [selectedRowKey, setSelectedRowKey] = useState<React.Key | null>(null);
  const [editingBookingId, setEditingBookingId] = useState<string | null>(null);
  const [now, setNow] = useState(Date.now());
  const [isCancelModalOpen, setCancelModalOpen] = useState(false);
  const lastCancelDataRef = useRef<any>(null);
  const tableClass = "booking-table";
  const assignedBy = getEmployeeName();
  const { Option } = Select;
  const selectedBranch = useBranchId();
  const [showFareDetails, setShowFareDetails] = useState(false);
  const [selectedFareDetail, setSelectedFareDetail] = useState<any>(null);
  const locationOptions = useLocationOptions();
  const suppressNextAssignRefresh = useRef(false);
  const [awaitingTableRefresh, setAwaitingTableRefresh] = useState(false);
  const [search, setSearch] = useState<any>("")

  const handleEditFareTypeChange = (value: any) => {
    const vehicleId = editForm.getFieldValue("vehicleid");
    setShowFareDetails([2, 3, 4].includes(Number(value)));
    editForm.setFieldsValue({ fareDetails: undefined });
    setSelectedFareDetail(null);

    if (!vehicleId) return;
    const numVal = Number(value);
    if (!value) { dispatch(resetFareEstimate()); return; }
    switch (numVal) {
      case 2: dispatch(getPackagesRequest(vehicleId, selectedBranch)); break;
      case 3: dispatch(getIntercityListRequest(vehicleId, selectedBranch)); break;
      case 4: dispatch(getNightFareRequest(vehicleId, selectedBranch)); break;
    }
  };

  useEffect(() => {
    if (selectedCab?.cabid) {
      setAssignOpen(true);
    }
  }, [selectedCab]);

  useEffect(() => {
    if (selectedCabError) {
      message.error(selectedCabError);
    }
  }, [selectedCabError]);

  useEffect(() => {
    if (autoAssignData) {
      message.success("Trip auto-assigned successfully!");
      setSelectedRowKey(null);
      suppressNextAssignRefresh.current = true;
      dispatch(getNewBookingsRequest(selectedBranch));
      dispatch(resetAutoAssignState());
    }
  }, [autoAssignData, dispatch, selectedBranch]);

  useEffect(() => {
    if (autoAssignError) {
      message.error(autoAssignError);
      dispatch(resetAutoAssignState());
    }
  }, [autoAssignError, dispatch]);

  useEffect(() => {
    if (editModalOpen) {
      if (!locations?.length) dispatch(getLocationsRequest(selectedBranch));
      if (!vehicleTypes?.length) dispatch(getVehicleTypesRequest(selectedBranch));
      if (!tripTypes?.length) dispatch(getTripTypesRequest());
      if (!goodsTypes?.length) dispatch(getGoodsTypesRequest());
    }
  }, [editModalOpen]);

  useEffect(() => {
    if (!editBooking) return;

    const dateTime = dayjs(editBooking.dateandtime);
    const tripTypeVal = String(editBooking.triptype) || undefined;
    const vehicleIdVal = String(editBooking.vehicleid) || undefined;
    const numTripType = Number(tripTypeVal);

    const fromLoc = (locations || []).find((l: any) => l.PlaceName === editBooking.fromloc);
    const toLoc = (locations || []).find((l: any) => l.PlaceName === editBooking.toloc);
    const goodsMatch = (goodsTypes || []).find((g: any) => g.goodsid === editBooking.goodsid);

    editForm.setFieldsValue({
      customername: editBooking.customername || "",
      mobileno: editBooking.mobileno || "",
      fromloc: editBooking.fromloc || "",
      toloc: editBooking.toloc || "",
      fromlat: editBooking.fromlat || fromLoc?.Latitude || undefined,
      fromlong: editBooking.fromlong || fromLoc?.Longitude || undefined,
      tolat: editBooking.tolat || toLoc?.Latitude || undefined,
      tolong: editBooking.tolong || toLoc?.Longitude || undefined,
      tripdate: dateTime.isValid() ? dateTime : dayjs(),
      triptime: dateTime.isValid() ? dateTime : dayjs(),
      vehicleid: vehicleIdVal,
      triptype: tripTypeVal,
      goodstype: goodsMatch?.goodsname || undefined,
      distance: editBooking.distance || "",
      alternativemobileno: editBooking.alternativemobileno || "",
      remarks: editBooking.remarks || "",
      features: [
        isFlagOn(editBooking.iscv) ? "CV" : null,
        isFlagOn(editBooking.istollgate) ? "Toll Gate" : null,
        isFlagOn(editBooking.isgst) || isFlagOn(editBooking.isintra) ? "Intra" : null,
        isFlagOn(editBooking.iscredit) ? "Prepaid" : null,
      ].filter(Boolean),
    });

    if ([2, 3, 4].includes(numTripType) && vehicleIdVal) {
      setShowFareDetails(true);
      editForm.setFieldsValue({ fareDetails: undefined });
      setSelectedFareDetail(null);
      switch (numTripType) {
        case 2: dispatch(getPackagesRequest(vehicleIdVal, selectedBranch)); break;
        case 3: dispatch(getIntercityListRequest(vehicleIdVal, selectedBranch)); break;
        case 4: dispatch(getNightFareRequest(vehicleIdVal, selectedBranch)); break;
      }
    } else {
      setShowFareDetails(false);
    }
  }, [editBooking, editForm]);

  useEffect(() => {
    if (!editBooking) return;
    const tripTypeVal = Number(editBooking.triptype);

    if (tripTypeVal === 3 && intercityList?.length) {
      const target = String(editBooking.intercitytype || "").trim();
      const match = intercityList.find((ic: any) => {
        const label = `${ic.basekm}-${ic.basefare} - ${convertMinutesToHrs(ic.baseminute)}`;
        return label.trim() === target;
      });
      if (match) {
        editForm.setFieldsValue({ fareDetails: match.tollerenckm });
        setSelectedFareDetail(match);
      }
    } else if (tripTypeVal === 2 && packages?.length) {
      const target = String(editBooking.packagetype || "").trim();
      const match = packages.find((pkg: any) => String(pkg.package || "").trim() === target);
      if (match) {
        editForm.setFieldsValue({ fareDetails: match.package });
        setSelectedFareDetail(match);
      }
    } else if (tripTypeVal === 4 && nightFare?.length) {
      const target = String(editBooking.nighttype || "").trim();
      const match = nightFare.find((nf: any) => String(nf.package || "").trim() === target);
      if (match) {
        editForm.setFieldsValue({ fareDetails: match.package });
        setSelectedFareDetail(match);
      }
    }
  }, [editBooking, intercityList, packages, nightFare, editForm]);
  useEffect(() => {
    if (updateBookingSuccess) {
      message.success("Booking updated successfully!");
      setEditModalOpen(false);
      editForm.resetFields();
      dispatch({ type: "UPDATE_NEW_BOOKING_RESET" });
      dispatch(getNewBookingEditReset());
      dispatch(getNewBookingsRequest(selectedBranch));
    } else if (updateBookingError) {
      message.error(updateBookingError);
      dispatch({ type: "UPDATE_NEW_BOOKING_RESET" });
    }
  }, [updateBookingSuccess, updateBookingError]);

  const handleEditBooking = (record: any) => {
    const bookingId = record.id || record.BookId || record.tripid;
    if (!bookingId) {
      message.warning("Booking ID not found.");
      return;
    }
    setEditingBookingId(String(bookingId));
    dispatch(getNewBookingEditRequest(String(bookingId)));
    setEditModalOpen(true);
  };

  const resolveFareType = (record: any): string => {
    if (!record) return "";
    if (record.faretype) return String(record.faretype);
    const trip = tripTypes?.find((t: any) => String(t.id) === String(record.triptype));
    return trip ? String(trip.name) : "";
  };

  const columns: TableProps<DataType>["columns"] = [
    {
      title: "Book ID",
      dataIndex: "id",
      key: "id",
      className: COPY_CELL,
      align: "center",
      width: 120,
      fixed: "left",
      onCell: (record: any) =>
        isAppBooking(record) ? { className: "cell-bookid-app" } : {},
      render: (text) => <strong style={{ color: "#89339c" }}>{text}</strong>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      className: COPY_CELL,
      align: "center",
      width: 150,
      fixed: "left",
      onCell: (record: any) =>
        isDelayedBooking(record.bookedtime, now)
          ? { className: "cell-name-delayed" }
          : {},
    },
    {
      title: "Mobile",
      dataIndex: "mobileno",
      key: "mobileno",
      className: COPY_CELL,
      align: "center",
      width: 120,
      fixed: "left",
      onCell: (record: any) =>
        isAdvanceBooking(record.bookedtime, now)
          ? { className: "cell-mobile-advanced" }
          : {},
    },
    {
      title: "Date",
      dataIndex: "bookedtime",
      key: "Date",
      align: "center",
      width: 120,
      render: (text) => text ? new Date(text).toLocaleDateString("en-GB") : "-",
    },
    {
      title: "Time",
      key: "time",
      align: "center",
      width: 150,
      render: (_: any, record: any) => (
        <div>
          {record.bookedtime && (
            <div>B - {dayjs(record.bookedtime).format("hh:mm A")}</div>
          )}
        </div>
      ),
    },
    {
      title: "From Location",
      dataIndex: "fromloc",
      key: "fromloc",
      align: "center",
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "To Location",
      dataIndex: "toloc",
      key: "toloc",
      align: "center",
      width: 150,
      ellipsis: true,
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <span>{text || "-"}</span>
        </Tooltip>
      ),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      align: "center",
      width: "auto",
    },
    {
      title: "Credit",
      dataIndex: "iscredit",
      key: "iscredit",
      align: "center",
      width: 100,
      render: (value: any) =>
        isFlagOn(value) ? (
          <Tooltip title="Credit">
            <CheckOutlined style={{ color: "#52c41a", fontSize: 16 }} />
          </Tooltip>
        ) : (
          "-"
        ),
    },
    {
      title: "Vehicle",
      dataIndex: "vehicletype",
      key: "vehicletype",
      align: "center",
      width: 120,
      render: (text: any, record: any) => {
        if (text) return text;
        const vehicle = vehicleTypes?.find((v: any) => String(v.id) === String(record.vehicletypeid));
        return vehicle ? vehicle.vehicletype : (record.vehicletypeid || "-");
      }
    },
    {
      title: "Fare Type",
      dataIndex: "faretype",
      key: "faretype",
      align: "center",
      width: "auto",
      render: (_: any, record: any) => resolveFareType(record) || record.triptype || "-"
    },
    {
      title: "Goods",
      dataIndex: "goods",
      key: "goods",
      align: "center",
      width: "auto",
    },
    {
      title: "Booked By",
      dataIndex: "bookedby",
      key: "bookedby",
      align: "center",
      width: "auto",
      render: (_: any, record: any) => (
        <div>
          {record.bookedby && <div>B - {record.bookedby}</div>}
          {record.updatedby && <div>U - {record.updatedby}</div>}
        </div>
      ),
    },
    // {
    //   title: "Customer ID",
    //   dataIndex: "",
    //   key: "",
    //   align: "center",
    //   width: "auto",
    // },
    {
      title: "Alternative",
      dataIndex: "alternativemobileno",
      key: "alternativemobileno",
      align: "center",
      width: 120,
    },
    {
      title: "Ref By",
      dataIndex: "refferedby",
      key: "refferedby",
      align: "center",
      width: 110,
    },
    {
      title: "City",
      dataIndex: "city",
      key: "city",
      align: "center",
      width: 130,
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 130,
      render: (_: any, record: any) => (
        <Button
          size="small"
          type="primary"
          icon={<EditOutlined />}
          disabled={editBookingLoading}
          style={{ background: "#89339C", border: "none" }}
          onClick={() => handleEditBooking(record)}
        />
      ),
    },
  ];

  const filteredBookings = newBookings.filter((newBooking: DataType) => {
    if (!search) return true;
    const lower = search.toLowerCase();
    return (
      newBooking.id?.toString().toLowerCase().includes(lower) ||
      newBooking.mobileno?.toString().toLowerCase().includes(lower) ||
      newBooking.name?.toString().toLowerCase().includes(lower)
    );
  });

  useMountEffectOnce(() => {
    dispatch(getNewBookingsRequest(selectedBranch));
    if (!vehicleTypes?.length) dispatch(getVehicleTypesRequest(selectedBranch));
    if (!locations?.length) dispatch(getLocationsRequest(selectedBranch));
  });

  useEffect(() => {
    let refreshTimer: number | undefined;
    const refreshSoon = () => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(
        () => dispatch(getNewBookingsRequest(selectedBranch)),
        NEW_SOCKET_REFRESH_DEBOUNCE_MS
      );
    };

    const handleBookingCreated = () => {
      refreshSoon();
    };

    const handleBookingAssigned = () => {
      if (suppressNextAssignRefresh.current) {
        suppressNextAssignRefresh.current = false;
        return;
      }
      refreshSoon();
    };

    socket.on("booking_created", handleBookingCreated);
    socket.on("booking_assigned", handleBookingAssigned);

    const onVisible = () => {
      if (document.visibilityState === "visible") {
        refreshSoon();
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      window.clearTimeout(refreshTimer);
      socket.off("booking_created", handleBookingCreated);
      socket.off("booking_assigned", handleBookingAssigned);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [dispatch]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(timer);
  }, []);

  const rowSelection = {
    type: "radio" as const,
    selectedRowKeys: selectedRowKey !== null ? [selectedRowKey] : [],
    onChange: (selectedRowKeys: React.Key[]) => {
      const newKey = selectedRowKeys.length > 0 ? selectedRowKeys[0] : null;
      setSelectedRowKey(newKey === selectedRowKey ? null : newKey);
    },
  };

  const selectedBooking = Array.isArray(newBookings)
    ? newBookings.find((b: any) => String(b.id ?? b.BookId ?? b._id ?? b.tripid) === String(selectedRowKey))
    : null;

  const isMeterFare =
    resolveFareType(selectedBooking).trim().toLowerCase() === "meter fare";

  const canAutoAssign = Boolean(selectedBooking) && isMeterFare;

  useEffect(() => {
    if (cancelData && lastCancelDataRef.current !== cancelData) {
      lastCancelDataRef.current = cancelData;
      message.success("Booking cancelled successfully!");
      setSelectedRowKey(null);
      setCancelModalOpen(false);
      dispatch(getNewBookingsRequest(selectedBranch));
      const retryTimer = window.setTimeout(() => {
        dispatch(getNewBookingsRequest(selectedBranch));
      }, 1200);
      return () => window.clearTimeout(retryTimer);
    }
  }, [cancelData, dispatch, selectedBranch]);

  useEffect(() => {
    if (error) {
      message.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (assignData) {
      message.success("Trip assigned successfully!");
      setSelectedRowKey(null);
      suppressNextAssignRefresh.current = true;
      setAwaitingTableRefresh(true);
      dispatch(getNewBookingsRequest(selectedBranch));
      dispatch({ type: "ASSIGN_TRIP_RESET" });
    }
  }, [assignData, dispatch, selectedBranch]);

  useEffect(() => {
    if (awaitingTableRefresh && !newBookingsLoading) {
      setAwaitingTableRefresh(false);
      setAssignOpen(false);
    }
  }, [awaitingTableRefresh, newBookingsLoading]);

  return (
    <div style={pageWrapper}>
      <Toolbar>
        <ToolbarGroup $maxGap={24}>
          {/* {canAutoAssign && (
            <Tooltip title="Auto Assign">
              <Button
                type="primary"
                shape="circle"
                icon={<ThunderboltOutlined />}
                style={{
                  background: "#1677ff",
                  borderColor: "#1677ff",
                }}
                loading={autoAssignLoading}
                disabled={autoAssignLoading}
                onClick={() => {
                  if (!selectedBooking) {
                    message.warning("Select a booking first.");
                    return;
                  }

                  const location = locations?.find(
                    (item: any) =>
                      String(item.PlaceName || "").trim().toLowerCase() ===
                      String(selectedBooking.fromloc || "").trim().toLowerCase()
                  );
                  const fromlat = selectedBooking.fromloclat || location?.Latitude;
                  const fromlong = selectedBooking.fromloclong || location?.Longitude;

                  if (!fromlat || !fromlong) {
                    message.error("The selected booking does not have a valid pickup location coordinate.");
                    return;
                  }

                  dispatch(
                    autoAssignTripRequest({
                      bookid: selectedBooking.id,
                      assignedby: getEmployeeName(),
                      emptyKM: 0,
                      cityid: selectedBooking.cityid || selectedBranch,
                      vehicletype: selectedBooking.vehicletypeid ?? selectedBooking.vehicletype,
                      fromlat,
                      fromlong,
                    })
                  );
                }}
              />
            </Tooltip>
          )} */}

          {/* <AssignModel
            open={assignOpen}
            setOpen={setAssignOpen}
            selectedRowKey={selectedRowKey !== null ? String(selectedRowKey) : null}
            selectedBookingData={selectedBooking ? {
              id: String(selectedBooking.id),
              fromloc: selectedBooking.fromloc,
              toloc: selectedBooking.toloc,
              remarks: selectedBooking.remarks,
              vehicletypeid: selectedBooking.vehicletypeid ?? selectedBooking.vehicletype,
              branchid: selectedBooking.cityid ?? selectedBranch,
              fromlat: selectedBooking.fromloclat ?? selectedBooking.fromlat,
              fromlong: selectedBooking.fromloclong ?? selectedBooking.fromlong,
            } : null}
            assignLoading={assignTripLoading || awaitingTableRefresh}
            onAssignConfirm={(rowKey, vehicleData) => {
              const booking = newBookings.find(
                (b: any) => b.BookId === rowKey || String(b.id) === rowKey || b._id === rowKey
              );
              if (!booking) { message.error("Booking not found"); return; }
              if (!vehicleData) { message.warning("Please select a vehicle"); return; }
              suppressNextAssignRefresh.current = true;
              dispatch(assignTripRequest({
                cabid: vehicleData.cabId,
                driverid: vehicleData.driverid,
                bookid: booking.id,
                assignedby: getEmployeeName(),
                emptyKM: vehicleData.distance,
              }));
            }}
          /> */}

          {/* <CancelAssign
            showCabId={false}
            selectedRowKey={selectedRowKey !== null ? String(selectedRowKey) : null}
            selectedBookingData={selectedBooking}
            isOpen={isCancelModalOpen}
            onOpenChange={setCancelModalOpen}
            confirmLoading={cancelLoading}
            onCancelConfirm={(rowKey, values) => {
              dispatch(cancelBookingRequest({
                bookingid: Number(values.bookingId),
                cancelreason: values.reason,
                cancelledby: getEmployeeName(),
                canceltype: 2,
                cabid: values.cabId || selectedBooking?.cabid || undefined,
                remarks: values.remarks || "",
              }));
            }}
          /> */}

          {/* <Search
            disabled={!selectedBooking}
            placeholder="Cab_ID Search.."
            enterButton={<SearchOutlined />}
            size="large"
            style={{ width: "clamp(120px, 11vw, 240px)" }}
            onSearch={(value) => {
              if (!selectedBooking) return;

              const location = locations?.find(
                (item: any) =>
                  String(item.PlaceName).trim().toLowerCase() ===
                  String(selectedBooking.fromloc).trim().toLowerCase()
              );

              const fromlat =
                selectedBooking.fromloclat || location?.Latitude || "";

              const fromlong =
                selectedBooking.fromloclong || location?.Longitude || "";

              if (!fromlat || !fromlong) {
                message.warning("Pickup location not found.");
                return;
              }

              dispatch(
                getSelectedCabRequest({
                  cabid: value.trim(),
                  fromlat,
                  fromlong,
                })
              );
            }}
            onClear={() => {
              setSearch("");
            }}
            allowClear
          /> */}

          <Tooltip title="Refresh">
            <Button
              icon={<RedoOutlined />}
              style={refreshBtn}
              onClick={() => {
                setSearch("");
                setSelectedRowKey(null);
                dispatch(getNewBookingsRequest(selectedBranch));
              }}
            />
          </Tooltip>
        </ToolbarGroup>

        <ToolbarGroup $maxGap={16}>
          <ActiveTripsPill>
            <CarOutlined />
            <span>Active Trips : <span className="pill-value">{todaycount || 0}</span></span>
          </ActiveTripsPill>

          <Search
            placeholder="Search bookings..."
            enterButton={<SearchOutlined />}
            size="large"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: "clamp(120px, 11vw, 240px)" }}
            onClear={() => {
              setSearch("");
            }}
            allowClear
          />
        </ToolbarGroup>
      </Toolbar>

      <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
        <ConfigProvider theme={noRowSelectionBgTheme}>
          <Table<DataType>
            bordered
            className={tableClass}
            rowSelection={rowSelection}
            rowClassName={(record) => {
              const classes: string[] = [];

              const v = record.vehicletype?.toLowerCase() || "";
              if (v.includes("bolero")) classes.push("bolero-row");
              else if (v.includes("407")) classes.push("row-407");
              else if (v.includes("eicher")) classes.push("row-eicher");

              if (isDelayedBooking(record.bookedtime, now)) classes.push("row-delayed");
              else if (isAdvanceBooking(record.bookedtime, now)) classes.push("row-advanced");

              return classes.join(" ");
            }}
            columns={columns}
            dataSource={
              Array.isArray(filteredBookings)
                ? filteredBookings.map((b: any) => ({
                  ...b,
                  key: b.id ?? b.BookId ?? b._id ?? b.tripid,
                }))
                : []
            }
            loading={newBookingsLoading}
            rowKey="key"
            scroll={{
              x: 2600,
              y: "calc(100vh - 320px)",
            }}
            pagination={{
              pageSizeOptions: ["5", "10", "100"],
              showSizeChanger: true,
              defaultPageSize: 100,
            }}
          />
        </ConfigProvider>
      </div>

      <Modal
        open={editModalOpen}
        destroyOnHidden
        onCancel={() => {
          setEditModalOpen(false);
          dispatch(getNewBookingEditReset());
          editForm.resetFields();
        }}
        footer={null}
        width={{ xs: "94vw", md: 680 }}
        styles={{ body: { padding: 0, background: " rgb(218 183 228)", borderRadius: "20px" } }}
        title={
          <div style={{
            background: "linear-gradient(135deg, #89339c, #b56ac8)",
            margin: "-20px -24px 0",
            padding: "18px 24px",
            borderRadius: "8px 8px 0 0",
            overflow: "hidden",
          }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", position: "relative", zIndex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 34, height: 34, background: "rgba(255,255,255,0.18)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <EditOutlined style={{ color: "#fff", fontSize: 18 }} />
                </div>
                <div>
                  <div style={{ color: "#fff", fontSize: 16, fontWeight: 500 }}>Edit Booking</div>
                  <div style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, marginTop: 2 }}>
                    {editBooking?.id || selectedRowKey}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      >
        {editBookingLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <Spin size="large" tip="Loading booking details..." />
          </div>
        ) : editBookingError ? (
          <div style={{ textAlign: "center", padding: 24, color: "red" }}>{editBookingError}</div>
        ) : (
          <Form form={editForm} layout="vertical" onFinish={(values) => {
            const features = values.features || [];
            const iscv = features.includes("CV");
            const istollgate = features.includes("Toll Gate");
            const iscredit = features.includes("Prepaid");
            const isgst = features.includes("Intra");
            const isintra = false;
            const fareType = Number(values.triptype);

            const intercityLabel = fareType === 3 && selectedFareDetail
              ? `${selectedFareDetail.basekm}-${selectedFareDetail.basefare} - ${convertMinutesToHrs(selectedFareDetail.baseminute)}`
              : values.fareDetails;

            dispatch(updateNewBookingRequest({
              bookid: editBooking?.id || editingBookingId,
              updatedby: getEmployeeName(),
              branchid: selectedBranch,
              mobileno: values.mobileno,
              name: values.customername,
              fromloc: values.fromloc,
              toloc: values.toloc,
              fromlat: values.fromlat,
              fromlong: values.fromlong,
              tolat: values.tolat,
              tolong: values.tolong,
              triptype: String(values.triptype),
              vechicletype: Number(values.vehicleid),
              goodstype: values.goodstype,
              remark: values.remarks,
              alternativemobno: values.alternativemobileno,
              distance: values.distance,
              fare: "0",
              tripdate: values.tripdate?.format("YYYY-MM-DD"),
              triptime: values.triptime?.format("HH:mm:ss"),
              packagetype: fareType === 2 ? values.fareDetails : undefined,
              intercitytype: fareType === 3 ? intercityLabel : undefined,
              nighttype: fareType === 4 ? values.fareDetails : undefined,
              iscredit,
              iscv,
              istollgate,
              isgst,
              isintra,
              noofvechicles: 1,
            }));
          }}
            style={{ padding: "14px 22px 0" }}>

            <div style={{ background: "#fff", border: "0.5px solid #e8e8e8", borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <div style={{ width: 3, height: 16, background: "#89339c", borderRadius: 0, flexShrink: 0 }} />
                <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: "#89339c" }}>
                  Customer info
                </span>
              </div>
              <Row gutter={[14, 10]}>
                <Col span={12}>
                  <Form.Item label="Customer name" name="customername" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <Input style={{ height: 36 }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Mobile number" name="mobileno" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <Input maxLength={10} style={{ height: 36 }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div style={{ background: "#fff", border: "0.5px solid #e8e8e8", borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <div style={{ width: 3, height: 16, background: "#89339c", borderRadius: 0, flexShrink: 0 }} />
                <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: "#89339c" }}>
                  Trip details
                </span>
              </div>
              <Row gutter={[14, 10]}>
                <Col span={12}>
                  <Form.Item label="From location" name="fromloc" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <LocationSelect
                      placeholder="From location"
                      style={{ height: 36 }}
                      options={locationOptions}
                      onPicked={(value) => {
                        const loc = (locations || []).find((l: any) => l.PlaceName === value);
                        editForm.setFieldsValue({
                          fromlat: loc?.Latitude ? String(loc.Latitude) : undefined,
                          fromlong: loc?.Longitude ? String(loc.Longitude) : undefined,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="To location" name="toloc" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <LocationSelect
                      placeholder="To location"
                      style={{ height: 36 }}
                      options={locationOptions}
                      onPicked={(value) => {
                        const loc = (locations || []).find((l: any) => l.PlaceName === value);
                        editForm.setFieldsValue({
                          tolat: loc?.Latitude ? String(loc.Latitude) : undefined,
                          tolong: loc?.Longitude ? String(loc.Longitude) : undefined,
                        });
                      }}
                    />
                  </Form.Item>
                </Col>
                <Form.Item name="fromlat" hidden><Input /></Form.Item>
                <Form.Item name="fromlong" hidden><Input /></Form.Item>
                <Col span={12}>
                  <Form.Item label="Date" name="tripdate" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <DatePicker style={{ width: "100%", height: 36 }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Time" name="triptime" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <TimePicker format="HH:mm" style={{ width: "100%", height: 36 }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Remarks" name="remarks" style={{ marginBottom: 0 }}>
                    <Input style={{ height: 36 }} />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div style={{ background: "#fff", border: "0.5px solid #e8e8e8", borderRadius: 12, padding: "14px 16px", marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <div style={{ width: 3, height: 16, background: "#89339c", borderRadius: 0, flexShrink: 0 }} />
                <span style={{ fontSize: 10, fontWeight: 500, letterSpacing: "0.07em", textTransform: "uppercase", color: "#89339c" }}>
                  Vehicle & options
                </span>
              </div>
              <Row gutter={[14, 10]}>
                <Col span={12}>
                  <Form.Item label="Vehicle type" name="vehicleid" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <Select
                      style={{ height: 36 }}
                      onChange={(val) => {
                        editForm.setFieldsValue({ fareDetails: undefined });
                        setSelectedFareDetail(null);
                        dispatch(resetFareEstimate());
                        dispatch(clearFareLists());
                        const currentFareType = Number(editForm.getFieldValue("triptype"));
                        if ([2, 3, 4].includes(currentFareType)) {
                          setShowFareDetails(true);
                          switch (currentFareType) {
                            case 2: dispatch(getPackagesRequest(val, selectedBranch)); break;
                            case 3: dispatch(getIntercityListRequest(val, selectedBranch)); break;
                            case 4: dispatch(getNightFareRequest(val, selectedBranch)); break;
                          }
                        }
                      }}
                    >
                      {vehicleTypes?.map((v: any) => <Option key={v.id} value={String(v.id)}>{v.vehicletype}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Goods type" name="goodstype" style={{ marginBottom: 0 }}>
                    <Select allowClear showSearch optionFilterProp="children" style={{ height: 36 }}>
                      {(goodsTypes || []).map((g: any) => (
                        <Option key={g.goodsid} value={g.goodsname}>{g.goodsname}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Fare type" name="triptype" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                    <Select
                      style={{ height: 36 }}
                      allowClear
                      onChange={(value) => {
                        handleEditFareTypeChange(value);
                      }}
                    >
                      {tripTypes?.map((t: any) => <Option key={t.id} value={String(t.id)}>{t.name}</Option>)}
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label="Add-ons" name="features" style={{ marginBottom: 0 }}>
                    <PillGroup />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div style={{ display: "flex", justifyContent: "center", gap: 10, padding: "14px 0 16px", borderTop: "0.5px solid #f0f0f0", marginTop: 4 }}>
              <Button type="primary" htmlType="submit" style={{
                height: 36, minWidth: 140, borderRadius: 8, fontWeight: 500, border: "none",
                background: "linear-gradient(135deg, #89339c, #b56ac8)"
              }}
                loading={updateBookingLoading}>
                Update booking
              </Button>

              <Button
                onClick={() => {
                  setEditModalOpen(false);
                  dispatch(getNewBookingEditReset());
                  editForm.resetFields();
                }}
                style={{ height: 36, borderRadius: 8, backgroundColor: "#f02929ce", color: "white" }}
              >
                Cancel
              </Button>
            </div>
          </Form>
        )}
      </Modal>
    </div>
  );
};

export default New;
